import React from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Play, 
  Pause,
  Mail,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { AlertRule } from '../../../types/solarMonitoring';

interface AlertRulesTabProps {
  alertRules: AlertRule[];
  onCreateRule: () => void;
  onEditRule: (rule: AlertRule) => void;
  onDeleteRule: (id: string) => void;
  onToggleRule: (id: string) => void;
}

export const AlertRulesTab: React.FC<AlertRulesTabProps> = ({ 
  alertRules, 
  onCreateRule,
  onEditRule,
  onDeleteRule,
  onToggleRule
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Alert Rules</h2>
        <button
          onClick={onCreateRule}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Create Rule</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {alertRules.map((rule) => (
          <div key={rule.id} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${
                  rule.isActive ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {rule.isActive ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{rule.name}</h3>
                  <p className="text-sm text-gray-600">
                    {rule.isActive ? 'Active' : 'Inactive'} • 
                    Max {rule.maxAlertsPerHour} alert(s) per hour
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onToggleRule(rule.id)}
                  className={`p-2 rounded-lg ${
                    rule.isActive 
                      ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200' 
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                  title={rule.isActive ? 'Pause Rule' : 'Activate Rule'}
                >
                  {rule.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => onEditRule(rule)}
                  className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                  title="Edit Rule"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDeleteRule(rule.id)}
                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                  title="Delete Rule"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Alert Recipients</h4>
                <div className="space-y-1">
                  {rule.alertEmails.map((email) => (
                    <div key={email} className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{email}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Time Ranges</h4>
                <div className="space-y-1">
                  {rule.timeRanges.map((range, index) => (
                    <div key={index} className="text-sm text-gray-600">
                      {range.startTime} - {range.endTime}: &lt; {range.thresholdPercent}% 
                      for {range.intervalMinutes}min
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};